/**
 * L.3: `WorkspaceFilePane` shell.
 *
 * Right-bottom optional pane that renders the single file currently
 * open in `useWorkspaceFileStore`. Returns null when no file is open
 * (so the chat pane can expand into the full width).
 *
 * Content dispatches by file extension to a viewer stub (L.3) which
 * later phases will replace with real viewers (AIEditor in 5.1, PDF
 * already exists, etc.).
 *
 * Keyboard: `Esc` when focus is inside the pane closes it and returns
 * focus to the trigger (layout-plan §7.5 focus rules).
 *
 * See: ai-docs/lexide-layout-plan.md §3.5
 */

import { useEffect, useRef } from "react";
import {
  basename,
  extension,
  useWorkspaceFileStore,
} from "@/stores/useWorkspaceFileStore";
import { viewerForExtension } from "./viewers";
import { ConversationPageViewer } from "./viewers/ConversationPageViewer";
import { useIsCompoundHead } from "@/hooks/useIsCompoundHead";
import { SourceAssessmentPane } from "./assessment/SourceAssessmentPane";
import { DocumentDetailsPane } from "./details/DocumentDetailsPane";

interface WorkspaceFilePaneProps {
  /** Optional override for the close-button + `Esc` handler. Default
   *  is `useWorkspaceFileStore.closeFile`. The real layout (L.4)
   *  overrides this to also call `navigate()` so the URL strips its
   *  `/files/...` suffix — the store→URL direction of the
   *  bidirectional sync (layout-plan §4.4). */
  onClose?: () => void;
}

export function WorkspaceFilePane({ onClose }: WorkspaceFilePaneProps = {}) {
  const activeFile = useWorkspaceFileStore((s) => s.activeFile);
  const storeCloseFile = useWorkspaceFileStore((s) => s.closeFile);
  const handleClose = onClose ?? storeCloseFile;
  const containerRef = useRef<HTMLElement | null>(null);

  // Esc closes the pane iff focus is inside it. Global Esc would
  // collide with dialogs, dropdowns, and the AIEditor's selection
  // clear shortcut (AIEditor plan §12). Scope to contained focus.
  useEffect(() => {
    if (!activeFile) return;
    const node = containerRef.current;
    if (!node) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && node?.contains(document.activeElement)) {
        e.preventDefault();
        handleClose();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeFile, handleClose]);

  const ext = activeFile ? extension(activeFile.path) : "";
  const Viewer = viewerForExtension(ext);
  // ⚠ Called UNCONDITIONALLY, above the early return — it is a hook, and the
  // `if (!activeFile) return null` that used to sit here would have made the
  // hook order depend on whether a file was open.
  const isHead = useIsCompoundHead(activeFile);

  if (!activeFile) return null;
  // Each viewer renders its own `ViewerInfoBar` chrome at the top
  // (filename chip + close + click-for-details, plus any viewer-
  // specific extras). The shell no longer renders any topbar of its
  // own — see `ViewerInfoBar` for the canonical chrome.

  return (
    <section
      ref={containerRef}
      data-testid="workspace-file-pane"
      data-file-path={activeFile.path}
      data-file-extension={ext}
      // ⚠ `data-file-extension` describes the FILE, which for a compound head
      // is the raw `.txt` — while the pane may be showing the assembled page.
      // Probes and specs read these attributes, so the head-ness is advertised
      // rather than left to be inferred from an extension that is now only half
      // the story.
      data-compound-head={isHead || undefined}
      role="main"
      aria-label={`Arquivo aberto: ${activeFile.title ?? basename(activeFile.path)}`}
      // `relative`: the SourceAssessmentPane's EXPANDED mode overlays this
      // whole section (absolute inset-0) — operator 2026-07-30.
      className="relative flex h-full min-h-0 flex-col bg-white dark:bg-slate-950"
      tabIndex={-1}
    >
      {/* `data-selectable-source` — text the lawyer selects anywhere
          in this container (PDF text layer, MD render, DOCX body, etc.)
          gets piped into `useSelectionContextStore` by the App-shell
          `useSelectionCapture` hook + surfaces as the
          `SelectionContextChip` below the composer. The file path is
          the source attribution shown in the chip. */}
      <div
        data-testid="workspace-file-pane-content"
        data-selectable-source={activeFile.path}
        className="min-h-0 flex-1 overflow-auto"
      >
        {/* ⚖ A compound HEAD opens its assembled page, not its raw export.
            Keyed on the catalog's `node_id` — the same fact that makes the row
            a head in the Acervo — never on a filename pattern, which would rot
            silently the next time `index-builder` renames something. The
            fallback passed in IS the extension viewer, so a pre-roll
            conversation with no page opens exactly as it does today rather
            than through a second guess at what "today" meant. */}
        {isHead ? (
          <ConversationPageViewer
            file={activeFile}
            onClose={handleClose}
            fallback={() => <Viewer file={activeFile} onClose={handleClose} />}
          />
        ) : (
          <Viewer file={activeFile} onClose={handleClose} />
        )}
      </div>
      {/* `.info` source-assessment pane — fixed at the bottom of the
          viewer for `sources/*` files only (returns null for raw-intake,
          artifacts, etc.). Operator brief 2026-06-27: a separate,
          contrasting-color pane below the viewer that renders the .info
          file content as HTML. */}
      <SourceAssessmentPane file={activeFile} />
      {/* A-14 `Detalhes` pane — the Generated-tier sibling of the assessment
          pane above (returns null outside `plans/`/`analyses/`/`output/`).
          Gates are disjoint by root, so the two never stack. */}
      <DocumentDetailsPane file={activeFile} />
    </section>
  );
}
